import { useState, useEffect, useCallback, useRef } from 'react'
import { ChevronDown, FileText, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import FaturaVendedorPreview from './FaturaVendedorPreview'

function Select({ label, value, onChange, children, disabled }) {
  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function FaturaVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,     setLeiloes]     = useState([])
  const [vendedores,  setVendedores]  = useState([])
  const [leilaoId,    setLeilaoId]    = useState('')
  const [vendedorId,  setVendedorId]  = useState('')
  const [loadingLeil, setLoadingLeil] = useState(false)
  const [loadingVend, setLoadingVend] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [dados,       setDados]       = useState(null)
  const [erro,        setErro]        = useState(null)
  const [cfgBlocos,   setCfgBlocos]   = useState({ medias_sexo: false, media_geral: false, dados_bancarios: false, ocultar_parcelas: false, modo_animais: null })
  const [colunasAtivas, setColunasAtivas] = useState(null)
  const [separacao,   setSeparacao]   = useState(null)

  // Separação da fatura é lembrada por leilão + vendedor (conveniência; só apresentação)
  const chaveSep = (l, v) => `fatura-vendedor-sep:${l}:${v}`
  const lerSep = (l, v) => {
    try { const r = localStorage.getItem(chaveSep(l, v)); return r ? JSON.parse(r) : null } catch { return null }
  }
  const salvarSep = (l, v, cfg) => {
    try { cfg ? localStorage.setItem(chaveSep(l, v), JSON.stringify(cfg)) : localStorage.removeItem(chaveSep(l, v)) } catch { /* ignora */ }
  }

  // Carrega leilões
  useEffect(() => {
    if (!orgId) return
    mapasService.getConfig(orgId).then(cfg => {
      setCfgBlocos({
        medias_sexo:     cfg.fatura_vendedor_mostrar_medias_sexo       === true,
        media_geral:     cfg.fatura_vendedor_mostrar_media_geral       === true,
        dados_bancarios: cfg.fatura_vendedor_dados_bancarios_vendedor  === true,
        ocultar_parcelas: cfg.fatura_vendedor_ocultar_parcelas        === true,
        modo_animais:    cfg.fatura_vendedor_total_animais || null,
      })
      if (cfg.fatura_vendedor_colunas_ativas) {
        setColunasAtivas(cfg.fatura_vendedor_colunas_ativas)
      }
    }).catch(() => {})
    setLoadingLeil(true)
    leiloesService.getByOrganizador(orgId, { status: 'on' })
      .then(list => {
        const sorted = [...list].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length > 0) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingLeil(false))
  }, [orgId])

  // Carrega vendedores quando leilão muda
  useEffect(() => {
    if (!orgId || !leilaoId) { setVendedores([]); return }
    setLoadingVend(true)
    setDados(null)
    mapasService.getVendedores(orgId, leilaoId)
      .then(list => { setVendedores(list); setVendedorId('') })
      .catch(() => setVendedores([]))
      .finally(() => setLoadingVend(false))
  }, [orgId, leilaoId])

  // Busca dados do preview
  const fetchSeq = useRef(0)
  const fetchDados = useCallback(async (vId, sep = null) => {
    if (!orgId || !leilaoId) return
    // Ignora respostas de requisições antigas (ex: "todos" lento terminando depois do vendedor escolhido)
    const seq = ++fetchSeq.current
    setLoadingPrev(true)
    setDados(null)
    setErro(null)
    try {
      const result = await mapasService.getFaturaVendedorDados(orgId, leilaoId, {
        vendedorId: vId || undefined,
        separacao: vId ? sep : null,
      })
      if (seq === fetchSeq.current) setDados(result)
    } catch (err) {
      if (seq === fetchSeq.current) setErro(err.response?.data?.detail || err.message || 'Erro ao carregar dados')
    } finally {
      if (seq === fetchSeq.current) setLoadingPrev(false)
    }
  }, [orgId, leilaoId])

  // Auto-fetch ao mudar vendedor
  useEffect(() => {
    if (!orgId || !leilaoId || loadingVend) return
    const sep = vendedorId ? lerSep(leilaoId, vendedorId) : null
    setSeparacao(sep)
    fetchDados(vendedorId, sep)
  }, [vendedorId, leilaoId, orgId, loadingVend]) // eslint-disable-line

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar a Fatura do Vendedor.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <FileText className="w-5 h-5 text-blue-600" />
        <h1 className="text-lg font-semibold text-gray-800">Fatura do Vendedor</h1>
        {loadingPrev && <RefreshCw className="w-4 h-4 animate-spin text-blue-500 ml-1" />}
      </div>

      {/* Filtros — linha única compacta */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">

          {/* Leilão */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Leilão" value={leilaoId}
              onChange={v => { setLeilaoId(v); setDados(null) }}
              disabled={loadingLeil}>
              {loadingLeil
                ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)
              }
            </Select>
          </div>

          {/* Vendedor */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Vendedor" value={vendedorId}
              onChange={v => { setVendedorId(v); setDados(null) }}
              disabled={loadingVend || !leilaoId}>
              <option value="">TODOS OS VENDEDORES</option>
              {[...vendedores]
                .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }))
                .map(v => (
                  <option key={v.id} value={String(v.id)}>{v.nome}</option>
                ))}
            </Select>
          </div>

        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{erro}</div>
      )}

      {/* Preview com scroll horizontal no mobile */}
      {dados && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <FaturaVendedorPreview
            dados={dados}
            orgId={orgId}
            leilaoId={leilaoId}
            vendedorId={vendedorId}
            showMediasSexo={cfgBlocos.medias_sexo}
            showMediaGeral={cfgBlocos.media_geral}
            showDadosBancarios={cfgBlocos.dados_bancarios}
            ocultarParcelas={cfgBlocos.ocultar_parcelas}
            modoAnimais={cfgBlocos.modo_animais}
            colunasAtivas={colunasAtivas}
            separacao={separacao}
            isencaoDefesaAtiva={organizadorAtivo?.produtos_premium?.includes('isencao_defesa')}
            onSeparar={(cfg) => { setSeparacao(cfg); salvarSep(leilaoId, vendedorId, cfg); fetchDados(vendedorId, cfg) }}
            onReload={() => fetchDados(vendedorId, separacao)}
          />
          </div>
        </div>
      )}

      {!dados && !loadingPrev && !erro && leilaoId && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400 text-sm">
          Selecione um vendedor acima para visualizar a fatura.
        </div>
      )}
    </div>
  )
}
